import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Chip,
  Typography,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  CardActions,
  Divider,
  Box,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
} from '@mui/material';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { campaignLeadManagementAtom, userInformationAtom } from '../../../../Services/_states';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmBox from '../../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { dateFormate } from '../../../../utils/helper.jsx';
import { ReportActions } from '../../../../Services/_actions/Report/report.action.js';
import { reportTemplateDetailsAtom } from '../../../../Services/_states/Report/index.ts';
import CampaignFormSelectionModal from '../../../LeadWebForm/CampaignFormSelectionModal.tsx';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TriggerManagerDialog from './TriggerManagerDialog.tsx';
import { globalpermissions } from '../../../../Services/Permission.js';
import { ConstantActions } from '../../../../Services/_actions/constant.actions.js';
import {
  getAuthenticatedOrganisationId,
  INTERVENE_DATA_SOURCE,
  LEADS_DATA_SOURCE,
} from '../../../../Services/_actions/Report/reportScope.js';

type TemplateItem = {
  _id: string;
  name: string;
  description?: string;
  created_at?: string;
  is_published?: boolean;
};

const ReportTemplates: React.FC<any> = () => {
  const actionsRef = useRef(ReportActions());
  const templatesRequestRef = useRef(0);
  const previewRequestRef = useRef(0);
  const reportActions = actionsRef.current;
  const navigate = useNavigate();
  const location = useLocation() as any;
  const setTemplateReport = useSetRecoilState(reportTemplateDetailsAtom);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const organisationId = getAuthenticatedOrganisationId(loggedInUser);
  const [dataSource, setDataSource] = useState(
    location.state?.data_source === INTERVENE_DATA_SOURCE
      ? INTERVENE_DATA_SOURCE
      : LEADS_DATA_SOURCE
  );
  const { showSnackBar } = useSnackBar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [campaignState, setCampaignState] = useRecoilState(campaignLeadManagementAtom);
  const [campaignFormModalProps, setCampaignFormModalProps] = useState({
    open: false,
    isEdit: false,
    formData: null,
  });
  const modalData = campaignFormModalProps?.formData;
  const selectedForm =
    campaignState?.selectedForm || modalData?.webForm || modalData?.campaign || null;
  const selectedFormId =
    selectedForm?.leads_form_id || selectedForm?.id || selectedForm?._id || null;
  const selectedFormName =
    selectedForm?.form_name || selectedForm?.leads_form_name || selectedForm?.name || null;
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  const [isPublish, setIsPublish] = useState(true);
  const leadId = null;
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [triggerTemplate, setTriggerTemplate] = useState<TemplateItem | null>(null);
  const constActions = ConstantActions();

  const handleOpenTriggerDialog = (t: TemplateItem) => {
    setTriggerTemplate(t);
    setTriggerDialogOpen(true);
  };

  const handleCloseTriggerDialog = (changed?: boolean) => {
    setTriggerDialogOpen(false);
    setTriggerTemplate(null);
    if (changed) getReportTemplates();
  };
  const getReportTemplates = async () => {
    const requestId = ++templatesRequestRef.current;
    if (dataSource === LEADS_DATA_SOURCE && !selectedFormId) {
      setTemplates([]);
      setLoading(false);
      showSnackBar('No selected form ID', 'error');
      return;
    }
    if (dataSource === INTERVENE_DATA_SOURCE && !organisationId) {
      setTemplates([]);
      setLoading(false);
      showSnackBar('Your account has no organisation for Intervene Analytics', 'error');
      return;
    }
    try {
      setLoading(true);
      const scope =
        dataSource === INTERVENE_DATA_SOURCE
          ? {
            data_source: INTERVENE_DATA_SOURCE,
            organisation_id: organisationId,
          }
          : selectedFormId;
      const res = await reportActions.getReportTemplates(scope, isPublish);
      if (requestId !== templatesRequestRef.current) return;
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setTemplates([...res?.data]);
      } else {
        setTemplates([]);
        showSnackBar('Failed to fetch report templates', 'error');
      }
    } catch (e) {
      if (requestId !== templatesRequestRef.current) return;
      setTemplates([]);
      showSnackBar('Failed to fetch report templates', 'error');
    } finally {
      if (requestId === templatesRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getReportTemplates();
  }, [isPublish, selectedFormId, dataSource, organisationId]);

  useEffect(() => {
    previewRequestRef.current += 1;
  }, [dataSource]);

  const toCreateFormat = () => {
    const analyticsScope =
      dataSource === INTERVENE_DATA_SOURCE
        ? {
          data_source: INTERVENE_DATA_SOURCE,
          scope_type: 'organisation',
          organisation_id: organisationId,
        }
        : { leads_form_id: selectedFormId ?? null };
    setTemplateReport(analyticsScope as any);
    navigate('/r/records/createReportFormat', {
      state: {
        leads_form_id: selectedFormId ?? null,
        leads_form_name: selectedFormName || null,
        ...analyticsScope,
      },
    });
  };

  const handleViewReport = async (t: TemplateItem) => {
    const requestId = ++previewRequestRef.current;
    try {
      setLoadingTemplateId(t._id);

      const res = await reportActions.getHtmlPreview(t._id, {
        parameters: {}, // no date range
        format: 'html',
        ...(dataSource === INTERVENE_DATA_SOURCE
          ? { organisation_id: organisationId }
          : {}),
      });
      if (requestId !== previewRequestRef.current) return;

      const ok = !!(res && (Array.isArray(res) || res?.status === 200 || res?.data));
      if (!ok) {
        showSnackBar('Failed to generate report preview', 'error');
        return;
      }

      showSnackBar('Report Preview generated successfully', 'success');
      navigate('/r/records/html-preview', {
        state: { name: t?.name, report: res?.data },
      });
    } catch (e) {
      if (requestId !== previewRequestRef.current) return;
      showSnackBar('Failed to Html Preview', 'error');
    } finally {
      setLoadingTemplateId(null);
    }
  };

  const handleDelete = (id: string) => {
    setSelectedTemplateId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    setConfirmOpen(false);

    if (!confirmed || !selectedTemplateId) {
      setSelectedTemplateId(null);
      return;
    }

    try {
      setLoadingTemplateId(selectedTemplateId);
      const res = await reportActions.deleteReport(selectedTemplateId);
      const success = !!(res && (res.status === 200 || res.ok || (res.data && !res.error)));
      if (success) {
        setTemplates((prev) => prev.filter((t) => t._id !== selectedTemplateId));
        showSnackBar('Template deleted successfully', 'success');
      } else {
        const msg =
          res?.data?.data?.general_error?.message ??
          res?.data?.message ??
          'Failed to delete template';
        showSnackBar(msg, 'error');
      }
    } catch (err) {
      showSnackBar(typeof err === 'string' ? err : 'Failed to delete template', 'error');
    } finally {
      setLoadingTemplateId(null);
      setSelectedTemplateId(null);
    }
  };

  const handleSubmitCampaignForm = (data) => {
    try {
      const leadFormsKey = 'leadWebFormData';
      const leadWebFormData = JSON.parse(localStorage.getItem(leadFormsKey) || 'null') || {};

      if (data && Object.keys(data).length > 0) {
        localStorage.setItem(leadFormsKey, JSON.stringify(data));
        setCampaignFormModalProps((prev) => ({
          ...prev,
          open: false,
          isEdit: false,
          formData: data,
        }));
      } else if (leadWebFormData?.campaign?.name && leadWebFormData?.webForm?.leads_form_name) {
        setCampaignFormModalProps((prev) => ({
          ...prev,
          open: false,
          isEdit: false,
          formData: leadWebFormData,
        }));
      } else {
        // no data provided and nothing in localStorage -> just close modal
        setCampaignFormModalProps((prev) => ({ ...prev, open: false }));
      }
    } catch (error) {
      console.warn(error, 'error');
    }
  };

  useEffect(() => {
    if (dataSource === INTERVENE_DATA_SOURCE) {
      setCampaignFormModalProps((prev) => ({ ...prev, open: false }));
      return;
    }
    try {
      const leadFormsKey = 'leadWebFormData';
      const leadWebFormData = JSON.parse(localStorage.getItem(leadFormsKey) || 'null') || {};
      if (
        !leadWebFormData ||
        Object.keys(leadWebFormData).length <= 1 ||
        (leadWebFormData?.campaign === null && leadWebFormData?.webForm === null)
      ) {
        setCampaignFormModalProps((prev) => ({ ...prev, open: true, isEdit: false }));
      } else {
        setCampaignFormModalProps((prev) => ({
          ...prev,
          open: false,
          formData: leadWebFormData,
          isEdit: false,
        }));
      }
    } catch (error) {
      console.warn(error, 'error');
      setCampaignFormModalProps((prev) => ({ ...prev, open: true, isEdit: false }));
    }
  }, [dataSource]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {campaignFormModalProps?.formData?.campaign?.name &&
          campaignFormModalProps?.formData?.webForm?.leads_form_name && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '30px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                mb: 0.5,
              }}
            >
              <IconButton
                onClick={() => {
                  navigate(-1);
                }}
                color="primary"
                size="medium"
                sx={{
                  marginRight: 2,
                  backgroundColor: '#e3f2fd',
                  '&:hover': { backgroundColor: '#bbdefb' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>

              <Typography
                component="span"
                variant="h5"
                sx={{ fontWeight: 'bold', color: '#333', fontSize: '14px' }}
              >
                {`${campaignFormModalProps?.formData?.campaign?.name} (${campaignFormModalProps?.formData?.webForm?.leads_form_name})`}
              </Typography>
              {constActions.userHasPermission([
                globalpermissions?.Reports?.editReport,
              ]) && (
                  <Box>
                    {!leadId && (
                      <IconButton
                        onClick={() => {
                          setCampaignFormModalProps((prev) => ({ ...prev, open: true, isEdit: true }));
                        }}
                        color="primary"
                        size="medium"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          '&:hover': { backgroundColor: '#bbdefb' },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>
                )}
            </Box>
          )}
      </Box>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Card
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <CardHeader
            title="Reports"
            subheader="Templates & quick actions"
            action={
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{
                  rowGap: 1,
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 'unset' },
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Data source"
                  value={dataSource}
                  onChange={(event) => setDataSource(event.target.value)}
                  sx={{ minWidth: 190 }}
                >
                  <MenuItem value={LEADS_DATA_SOURCE}>Business Service Leads</MenuItem>
                  <MenuItem value={INTERVENE_DATA_SOURCE}>Intervene Analytics</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!isPublish}
                      onChange={(e) => setIsPublish(!e.target.checked)}
                      inputProps={{ 'aria-label': 'controlled' }}
                      size="small"
                    />
                  }
                  sx={{ m: 0 }}
                  label={!isPublish ? `All Reports` : `All Published Reports`}
                />
                {constActions.userHasPermission([
                  globalpermissions?.Reports?.createReport,
                ]) && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={toCreateFormat}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      Create Report Format
                    </Button>
                  )}
              </Stack>
            }
            sx={{
              '.MuiCardHeader-content': { minWidth: 0 },
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
            }}
          />

          <CardContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            {loading ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ minHeight: 120, width: '100%' }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2">Loading templates…</Typography>
              </Stack>
            ) : templates.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 160, textAlign: 'center', width: '100%' }}
                spacing={1}
              >
                <Typography variant="body2" color="text.secondary">
                  No templates found.
                </Typography>
                <Button
                  onClick={toCreateFormat}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5, width: { xs: '100%', sm: 'auto' } }}
                  startIcon={<AddIcon />}
                >
                  Create Report Format
                </Button>
              </Stack>
            ) : (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {templates.map((t) => {
                  const isBusy = loadingTemplateId === t._id;
                  return (
                    // <Grid key={t._id} item xs={12} sm={6} md={4} lg={3}>
                    //   <Card
                    //     variant="outlined"
                    //     sx={{
                    //       height: '100%',
                    //       display: 'flex',
                    //       flexDirection: 'column',
                    //       position: 'relative',
                    //       width: '100%',
                    //     }}
                    //   >
                    //     <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1.5 }}>
                    //       <Box sx={{ flex: 1, minWidth: 0 }}>
                    //         <Typography variant="subtitle1" noWrap title={t.name}>
                    //           {t.name || 'Template'}
                    //         </Typography>
                    //         <Typography
                    //           variant="body2"
                    //           color="text.secondary"
                    //           noWrap
                    //           title={t.description || ''}
                    //           sx={{ mt: 0.25 }}
                    //         >
                    //           {t.description || '—'}
                    //         </Typography>
                    //       </Box>

                    //       <Tooltip title="Delete Template">
                    //         <span>
                    //           <IconButton
                    //             size="small"
                    //             color="error"
                    //             onClick={() => handleDelete(t._id)}
                    //           >
                    //             <DeleteIcon fontSize="small" />
                    //           </IconButton>
                    //         </span>
                    //       </Tooltip>
                    //       <Tooltip title="Open Template">
                    //         <span>
                    //           <IconButton
                    //             size="small"
                    //             onClick={() =>
                    //               navigate(`/r/records/editReportFormat/${t._id}`, {
                    //                 state: {
                    //                   id: t._id,
                    //                   leads_form_id: selectedFormId,
                    //                   leads_form_name: selectedFormName,
                    //                 },
                    //               })
                    //             }
                    //             disabled={isBusy}
                    //           >
                    //             <SettingsIcon fontSize="small" />
                    //           </IconButton>
                    //         </span>
                    //       </Tooltip>
                    //     </Box>

                    //     <CardContent sx={{ pt: 1.5, pb: 1.5, flex: 1 }}>
                    //       <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    //         <Chip
                    //           size="small"
                    //           label={t.is_published ? 'Published' : 'Unpublish'}
                    //           color={t.is_published ? 'success' : 'default'}
                    //           variant={t.is_published ? 'filled' : 'outlined'}
                    //         />
                    //       </Stack>

                    //       <Divider sx={{ my: 1 }} />

                    //       <Typography variant="caption" color="text.secondary">
                    //         Created:&nbsp;{dateFormate(t.created_at, 'DD-MM-YYYY, hh:mm A')}
                    //       </Typography>
                    //     </CardContent>

                    //     <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                    //       <Button
                    //         size="small"
                    //         onClick={() => handleViewReport(t)}
                    //         disabled={isBusy}
                    //         sx={{ width: { xs: '100%', sm: 'auto' } }}
                    //       >
                    //         View Report
                    //       </Button>
                    //     </CardActions>
                    //   </Card>
                    // </Grid>
                    <Grid item xs={12} sm={6} md={3} key={t._id}>
                      <Card
                        elevation={0}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          backgroundColor: '#ffffff',
                          border: '2px solid #3b82f6',
                          borderRadius: 2,
                          overflow: 'hidden',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
                            borderColor: '#3b82f6',
                          },
                        }}
                      >
                        {/* Card Content */}
                        <Box
                          sx={{
                            p: { xs: 2, sm: 2.5 },
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* Header */}
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{ mb: 2 }}
                          >
                            <Typography
                              variant="subtitle1"
                              noWrap
                              title={t.name}
                              sx={{ fontWeight: 'bold' }}
                            >
                              {t.name || 'Template'}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={t.description || ''}
                              sx={{ mt: 0.25 }}
                            >
                              {t.description || '—'}
                            </Typography>

                            {/* Action Buttons */}
                            <Box display="flex" gap={0.5}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`/r/records/editReportFormat/${t._id}`, {
                                    state: {
                                      id: t._id,
                                      leads_form_id: selectedFormId,
                                      leads_form_name: selectedFormName,
                                      data_source: dataSource,
                                      organisation_id: organisationId,
                                    },
                                  })
                                }
                                disabled={isBusy}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 1.5,
                                  '&:hover': {
                                    backgroundColor: '#e2e8f0',
                                    borderColor: '#cbd5e1',
                                  },
                                }}
                              >
                                <SettingsIcon sx={{ fontSize: 14, color: '#64748b' }} />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => handleDelete(t._id)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: '#fef2f2',
                                  border: '1px solid #fecaca',
                                  borderRadius: 1.5,
                                  '&:hover': {
                                    backgroundColor: '#fee2e2',
                                    borderColor: '#fca5a5',
                                  },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                              </IconButton>
                            </Box>
                          </Box>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewReport(t)}
                              disabled={isBusy}
                              sx={{ minWidth: 110 }}
                            >
                              View Report
                            </Button>

                            <Button
                              variant="outlined"
                              size="small"
                              disabled={isBusy}
                              sx={{ minWidth: 110 }}
                              onClick={() =>
                                navigate(`/r/records/templates/${t._id}/triggers/list`, {
                                  state: {
                                    templateName: t.name,
                                    leads_form_id: selectedFormId,
                                    leads_form_name: selectedFormName,
                                  },
                                })
                              }
                            >
                              Trigger Report
                            </Button>
                          </Stack>
                        </Box>

                        {/* Footer */}
                        <Box
                          sx={{
                            pt: 1,
                            borderTop: '1px solid #f3f4f6',
                            mt: 'auto',
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                          >
                            <Box display="flex" alignItems="center" gap={1} paddingLeft={3}>
                              <Typography variant="caption" color="text.secondary">
                                Created:&nbsp;{dateFormate(t.created_at, 'DD-MM-YYYY, hh:mm A')}
                              </Typography>
                            </Box>
                            <Chip
                              sx={{ marginRight: '17px' }}
                              size="small"
                              label={t.is_published ? 'Published' : 'Unpublish'}
                              color={t.is_published ? 'success' : 'default'}
                              variant={t.is_published ? 'filled' : 'outlined'}
                            />
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>

        <ConfirmBox
          open={confirmOpen}
          onClose={handleConfirmDelete}
          options={{
            title: 'Delete Report?',
            text: 'Are you sure you want to delete this report?',
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'error',
            cancelButtonColor: 'default',
          }}
        />
        {dataSource === LEADS_DATA_SOURCE && campaignFormModalProps?.open && (
          <CampaignFormSelectionModal
            open={campaignFormModalProps?.open}
            onClose={handleSubmitCampaignForm}
            isEdit={campaignFormModalProps?.isEdit || false}
            selectedCampaign={campaignFormModalProps?.formData?.campaign || null}
            selectedForm={campaignFormModalProps?.formData?.webForm || null}
          />
        )}
        {triggerDialogOpen && triggerTemplate && (
          <TriggerManagerDialog
            open={triggerDialogOpen}
            onClose={handleCloseTriggerDialog}
            templateId={triggerTemplate._id}
            templateName={triggerTemplate.name}
          />
        )}
      </Box>
    </>
  );
};

export default ReportTemplates;
